import { ArrowNarrowRightIcon } from '@langchain/untitled-ui-icons';

import { useMemo } from 'react';

import { ExpandableErrorAlert } from '@/components/ExpandableErrorAlert';
import { SingleFeedbackChipWithSourceRunAction } from '@/components/FeedbackChips';
import { IconButton, Tooltip } from '@/design-system';
import { ExperimentViewOverridesSchema, RunSchema } from '@/types/schema';
import { isEvaluatorRunExpired } from '@/utils/isEvaluatorRunExpired';
import { cn } from '@/utils/tailwind';

import { useFeedbackSourceRunIds } from '../../hooks/useFeedbackSourceRunIds';
import { OnlyErrorsChip } from './OnlyErrorsChip';

export const SingleFeedbackWithSource = ({
  feedbackKey,
  run,
  setOpenRunId,
  chipClassName,
  valueClassName,
  isHeatmapVisible,
  maxWidth,
  isTextTruncated,
  onMutate,
  traceTier,
  experimentStartTime,
  columnOverride,
}: {
  feedbackKey: string;
  run: RunSchema;
  setOpenRunId: (runId: string | null) => void;
  chipClassName?: string;
  valueClassName?: string;
  isHeatmapVisible: boolean;
  maxWidth?: number;
  isTextTruncated?: boolean;
  onMutate?: () => void;
  traceTier?: string | null;
  experimentStartTime?: string | null;
  columnOverride?: ExperimentViewOverridesSchema['column_overrides'][number];
}) => {
  const feedbackSourceRunIds = useFeedbackSourceRunIds(
    feedbackKey,
    [run.id],
    null
  );
  const feedbackStats = run.feedback_stats?.[feedbackKey];
  const isCompositeFeedback = feedbackStats?.sources?.some(
    (source) => source.type === '__ls_composite_feedback'
  );

  const feedbackSourceRun = feedbackSourceRunIds.data?.[0];

  const runComment = feedbackSourceRun?.comment;
  const feedbackComment = feedbackStats?.comments?.[0]?.replace(
    /^["']|["']$/g,
    ''
  );

  // Use the utility function to check if the evaluator run has expired
  const isRunExpired = useMemo(
    () => isEvaluatorRunExpired(traceTier, experimentStartTime),
    [traceTier, experimentStartTime]
  );

  return (
    <div className="flex w-full items-center gap-2">
      <div
        onMouseEnter={() => {
          if (!feedbackSourceRunIds.data) {
            feedbackSourceRunIds.trigger();
          }
        }}
      >
        {feedbackStats?.n ||
        (feedbackStats?.values &&
          Object.keys(feedbackStats?.values).length > 0) ? (
          <SingleFeedbackChipWithSourceRunAction
            feedbackKey={feedbackKey}
            avg={feedbackStats?.avg}
            n={feedbackStats?.n}
            values={feedbackStats?.values}
            errors={feedbackStats?.errors}
            feedbackSourceRunId={run.id}
            feedbackSourceRuns={[run]}
            hideFeedbackKey
            hideEllipsis
            startDecorator={null}
            chipClassName={chipClassName}
            valueClassName={valueClassName}
            tooltip={runComment}
            onMutate={onMutate}
            columnOverride={columnOverride}
            disablePopover={isCompositeFeedback}
          />
        ) : feedbackComment ? (
          feedbackStats?.errors ? (
            <Error
              text={feedbackComment}
              isTextTruncated={isTextTruncated}
              maxWidth={maxWidth}
            />
          ) : (
            <Text
              text={feedbackComment}
              isTextTruncated={isTextTruncated}
              maxWidth={maxWidth}
            />
          )
        ) : (
          <OnlyErrorsChip
            tooltipText={runComment ?? feedbackKey}
            numErrors={feedbackStats?.errors ?? 0}
            isHeatmapVisible={isHeatmapVisible}
            hideBlankScore={!isHeatmapVisible}
          />
        )}
      </div>
      {feedbackStats?.show_feedback_arrow && (
        <IconButton
          icon={ArrowNarrowRightIcon}
          label={isRunExpired ? 'Evaluator Run Expired' : 'View Evaluator Run'}
          disabled={isRunExpired}
          onClick={async () => {
            if (!feedbackSourceRunIds.data) {
              await feedbackSourceRunIds.trigger();
            }
            const runId =
              feedbackSourceRunIds.data?.[0]?.feedback_source?.metadata?.__run
                ?.run_id;
            if (runId) {
              setOpenRunId(runId);
            }
          }}
          loading={feedbackSourceRunIds.isMutating}
          color="secondary"
          className="invisible group-hover:visible"
        />
      )}
    </div>
  );
};

function Text({
  text,
  isTextTruncated,
  maxWidth,
  className,
}: {
  text: string;
  isTextTruncated?: boolean;
  maxWidth?: number;
  className?: string;
}) {
  return (
    <Tooltip
      title={text}
      tooltipClassName="max-h-[300px] max-w-[350px] overflow-auto"
      delayDuration={0}
    >
      <div
        className={cn(
          'truncate text-sm',
          !isTextTruncated && 'text-wrap',
          className
        )}
        style={{ maxWidth: maxWidth ? maxWidth - 50 : undefined }}
      >
        {text}
      </div>
    </Tooltip>
  );
}

function Error({
  text,
  isTextTruncated,
  maxWidth,
}: {
  text: string;
  isTextTruncated?: boolean;
  maxWidth?: number;
}) {
  return isTextTruncated ? (
    <Text
      text={text}
      className="font-medium text-error"
      isTextTruncated={isTextTruncated}
      maxWidth={maxWidth}
    />
  ) : (
    <ExpandableErrorAlert error={text} />
  );
}
